import { useDispatch } from "react-redux";
import { trackEvent } from "src/Amplitude";
import { SanitisedTradeSummaryRes } from "src/store/features/api/generated";
import { setBuyInput, setSellInput } from "src/store/features/swap.slice";

interface useTradeSummaryProps {
  buyInput: Record<string, any>;
}
const useTradeSummary = (props: useTradeSummaryProps) => {
  const dispatch = useDispatch();

  /**
   * Updates the trade summary state and dispatches actions to update the buy and sell input states.
   *
   * This function takes a summary object and updates the Trade Card component summary with the new summary data.
   * It sets the summary state with detailed information including total value, expected output, and fees.
   * Additionally, it updates the sell and buy input states with the provided units and values.
   * If the known side of the trade is "sell", it updates the sell input's approx value.
   * If the known side is "buy", it updates the buy input's approx value.
   */
  const handleSummaryChange = (summary: SanitisedTradeSummaryRes): void => {
    dispatch(
      setSellInput({
        value: summary.sell.value.toString(),
        units: summary.sell.units.toString(),
      }),
    );
    dispatch(
      setBuyInput({
        value: summary.buy.value.toString(),
        units: summary.buy.units.toString(),
      }),
    );

    if (summary.knownSide === "sell") {
      dispatch(
        setSellInput({
          approx: summary.sell.units.toString(),
        }),
      );
    } else {
      dispatch(
        setBuyInput({
          approx: summary.buy.units.toString(),
        }),
      );
    }

    trackEvent(
      "User View Trade Summary",
      {
        sellUnits: summary.sell.units,
        buyUnits: summary.buy.units,
        buyValue: summary.buy.value,
        fee: summary.fee?.amount ?? 0,
        total: summary.totalValue,
        currentBuyPrice: summary.buy.price,
      },
      "Trade",
    );
  };

  return { handleSummaryChange };
};

export default useTradeSummary;
